/**
 * CitationMarker - Inline [n] marker with hover tooltip
 *
 * Shows citation number inline in text.
 * On hover: displays tooltip with source snippet.
 * On click: triggers callback to highlight source in left panel.
 */

import { useState, useRef, useEffect } from 'react';
import { KnowledgeCitation } from '@deep-agents/shared-types';

interface CitationMarkerProps {
  /** The citation index (1, 2, 3, etc.) */
  index: number;
  /** The citation data (may be undefined if index doesn't match) */
  citation?: KnowledgeCitation;
  /** The text from the answer that this citation supports */
  citedText?: string;
  /** Called when user clicks the marker */
  onClick?: () => void;
}

export function CitationMarker({ index, citation, citedText, onClick }: CitationMarkerProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<'above' | 'below'>('above');
  const [horizontalOffset, setHorizontalOffset] = useState(0);
  const markerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Calculate tooltip position based on available space
  useEffect(() => {
    if (showTooltip && markerRef.current) {
      const rect = markerRef.current.getBoundingClientRect();
      const spaceAbove = rect.top;

      // Show below if not enough space above (200px threshold)
      setTooltipPosition(spaceAbove < 200 ? 'below' : 'above');

      // Calculate horizontal offset to keep tooltip in viewport
      // Tooltip is 340px wide, centered on marker
      const tooltipWidth = 340;
      const markerCenter = rect.left + rect.width / 2;
      const tooltipLeft = markerCenter - tooltipWidth / 2;
      const tooltipRight = markerCenter + tooltipWidth / 2;
      const viewportPadding = 12;

      if (tooltipLeft < viewportPadding) {
        // Would overflow left - shift right
        setHorizontalOffset(viewportPadding - tooltipLeft);
      } else if (tooltipRight > window.innerWidth - viewportPadding) {
        // Would overflow right - shift left
        setHorizontalOffset(window.innerWidth - viewportPadding - tooltipRight);
      } else {
        setHorizontalOffset(0);
      }
    }
  }, [showTooltip]);

  const handleMouseEnter = () => {
    if (citation?.snippet) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const handleClick = () => {
    onClick?.();
  };

  /**
   * Extract the most relevant quote from a longer snippet.
   * Tries to find complete sentences and pick the most informative one.
   */
  const extractKeyQuote = (snippet: string, maxLength = 180): string => {
    if (!snippet) return 'Source not available';

    // Clean up the snippet - remove excessive whitespace and page markers
    const cleaned = snippet
      .replace(/---\s*PAGE\s*\d+\s*---/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (cleaned.length <= maxLength) return cleaned;

    // Split into sentences
    const sentences = cleaned.split(/(?<=[.!?])\s+/);

    if (sentences.length === 0) {
      return cleaned.slice(0, maxLength) + '...';
    }

    // If first sentence fits, use it (often the most relevant)
    if (sentences[0].length <= maxLength) {
      // Try to include more sentences if they fit
      let result = sentences[0];
      for (let i = 1; i < sentences.length && i < 3; i++) {
        if (result.length + sentences[i].length + 1 <= maxLength) {
          result += ' ' + sentences[i];
        } else {
          break;
        }
      }
      return result;
    }

    // First sentence too long - truncate at a word boundary
    const truncated = cleaned.slice(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    if (lastSpace > maxLength * 0.6) {
      return truncated.slice(0, lastSpace) + '...';
    }
    return truncated + '...';
  };

  const keyQuote = extractKeyQuote(citation?.snippet || '');

  // Extract a clean document name from the documentId (often a file path or long ID)
  const getDocumentDisplayName = (docId: string | undefined): string => {
    if (!docId) return 'Unknown source';
    // If it looks like a file path, get the filename
    const lastSlash = Math.max(docId.lastIndexOf('/'), docId.lastIndexOf('\\'));
    const name = lastSlash >= 0 ? docId.slice(lastSlash + 1) : docId;
    // Truncate if too long
    return name.length > 40 ? name.slice(0, 37) + '...' : name;
  };

  const documentName = getDocumentDisplayName(citation?.documentId);

  return (
    <span className="citation-marker-wrapper">
      <button
        ref={markerRef}
        type="button"
        className="citation-marker"
        data-index={index <= 5 ? index : undefined}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        title={citation ? `Source: ${citation.documentId}` : 'Source not available'}
      >
        {index}
      </button>

      {/* Tooltip */}
      {showTooltip && (
        <div
          ref={tooltipRef}
          className={`citation-tooltip ${tooltipPosition}`}
          style={{
            transform: `translateX(calc(-50% + ${horizontalOffset}px))`
          }}
        >
          {/* Header with source info */}
          <div className="tooltip-header">
            <span className="tooltip-index" data-index={index <= 5 ? index : undefined}>{index}</span>
            <span className="tooltip-source">{documentName}</span>
          </div>

          {/* Show the claim from the answer if available */}
          {citedText && (
            <div className="tooltip-section">
              <div className="tooltip-label">Claim:</div>
              <div className="tooltip-claim">"{citedText.length > 120 ? citedText.slice(0, 117) + '...' : citedText}"</div>
            </div>
          )}

          {/* Show the evidence from the source */}
          <div className="tooltip-section">
            <div className="tooltip-label">Evidence from source:</div>
            <div className="tooltip-evidence">"{keyQuote}"</div>
          </div>

          <div className="tooltip-hint">
            Click to view full source
          </div>
        </div>
      )}

      <style>{`
        .citation-marker-wrapper {
          position: relative;
          display: inline;
        }

        /* Dark theme citation marker */
        .citation-marker {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 18px;
          height: 18px;
          padding: 0 5px;
          margin: 0 3px;
          background: rgba(99, 102, 241, 0.2);
          border: none;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          color: #a5b4fc;
          cursor: pointer;
          transition: all 0.15s;
          vertical-align: middle;
          font-family: inherit;
          line-height: 1;
        }

        .citation-marker:hover {
          background: rgba(99, 102, 241, 0.35);
          color: #c7d2fe;
          transform: scale(1.05);
        }

        /* Different colors for different citation numbers - dark theme optimized */
        .citation-marker[data-index="1"] { background: rgba(99, 102, 241, 0.2); color: #a5b4fc; }
        .citation-marker[data-index="2"] { background: rgba(59, 130, 246, 0.2); color: #93c5fd; }
        .citation-marker[data-index="3"] { background: rgba(16, 185, 129, 0.2); color: #6ee7b7; }
        .citation-marker[data-index="4"] { background: rgba(245, 158, 11, 0.2); color: #fcd34d; }
        .citation-marker[data-index="5"] { background: rgba(239, 68, 68, 0.2); color: #fca5a5; }

        .citation-marker[data-index="1"]:hover { background: rgba(99, 102, 241, 0.35); }
        .citation-marker[data-index="2"]:hover { background: rgba(59, 130, 246, 0.35); }
        .citation-marker[data-index="3"]:hover { background: rgba(16, 185, 129, 0.35); }
        .citation-marker[data-index="4"]:hover { background: rgba(245, 158, 11, 0.35); }
        .citation-marker[data-index="5"]:hover { background: rgba(239, 68, 68, 0.35); }

        /* User bubble citation markers - higher contrast */
        .user-bubble .citation-marker {
          background: rgba(255, 255, 255, 0.2);
          color: rgba(255, 255, 255, 0.95);
        }

        .user-bubble .citation-marker:hover {
          background: rgba(255, 255, 255, 0.35);
          color: #fff;
        }

        .citation-tooltip {
          position: absolute;
          left: 50%;
          /* transform applied via inline style for dynamic offset */
          width: 340px;
          max-width: calc(100vw - 24px);
          padding: 14px;
          background: #1a1a1f;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
          z-index: 1000;
          animation: tooltipFadeIn 0.15s ease-out;
        }

        .citation-tooltip.above {
          bottom: calc(100% + 8px);
        }

        .citation-tooltip.below {
          top: calc(100% + 8px);
        }

        /* Arrow */
        .citation-tooltip::after {
          content: '';
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          border: 6px solid transparent;
        }

        .citation-tooltip.above::after {
          top: 100%;
          border-top-color: #1a1a1f;
        }

        .citation-tooltip.below::after {
          bottom: 100%;
          border-bottom-color: #1a1a1f;
        }

        @keyframes tooltipFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .tooltip-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }

        .tooltip-index {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          height: 20px;
          padding: 0 6px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          background: rgba(196, 181, 253, 0.2);
          color: #c4b5fd;
        }

        .tooltip-index[data-index="1"] { background: rgba(124, 58, 237, 0.3); color: #c4b5fd; }
        .tooltip-index[data-index="2"] { background: rgba(59, 130, 246, 0.3); color: #93c5fd; }
        .tooltip-index[data-index="3"] { background: rgba(16, 185, 129, 0.3); color: #6ee7b7; }
        .tooltip-index[data-index="4"] { background: rgba(245, 158, 11, 0.3); color: #fcd34d; }
        .tooltip-index[data-index="5"] { background: rgba(239, 68, 68, 0.3); color: #fca5a5; }

        .tooltip-source {
          font-size: 11px;
          color: #9ca3af;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .tooltip-section {
          margin-top: 10px;
        }

        .tooltip-label {
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #6b7280;
          margin-bottom: 4px;
        }

        .tooltip-claim {
          font-size: 12px;
          line-height: 1.4;
          color: #a5b4fc;
          font-style: italic;
          padding: 8px;
          background: rgba(99, 102, 241, 0.1);
          border-radius: 6px;
          border-left: 2px solid #6366f1;
        }

        .tooltip-evidence {
          font-size: 12px;
          line-height: 1.4;
          color: #86efac;
          font-style: italic;
          padding: 8px;
          background: rgba(34, 197, 94, 0.1);
          border-radius: 6px;
          border-left: 2px solid #22c55e;
        }

        .tooltip-hint {
          margin-top: 10px;
          font-size: 10px;
          color: #6b7280;
          text-align: right;
        }
      `}</style>
    </span>
  );
}
